import { getLocalizedStringValue } from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, IconFormIcons } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Paths } from 'shared/routes/paths';
import { ID, LocalizedLifeArea } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import * as Styled from './search-life-area-card.style';

export type SearchLifeAreaCardProps = {
  lifeArea: LifeArea | LocalizedLifeArea;
  onClick?: (id: ID) => void;
  tabbable?: boolean;
  navTo?: string;
};

export const SearchLifeAreaCard: React.FC<SearchLifeAreaCardProps> = ({
  lifeArea,
  onClick,
  tabbable,
  navTo,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const locale = useI18nLocale();

  const _onClick = (e: React.MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(navTo ?? Paths.LifeAreaDetail.replace(':id', lifeArea.id));
    onClick?.(lifeArea.id);
  };

  const name = useMemo(
    () => getLocalizedStringValue(lifeArea.name, locale),
    [lifeArea.name, locale],
  );

  useKeysDown(
    ['Enter'],
    (e) => {
      if (tabbable && document.activeElement === containerRef.current) {
        _onClick(e);
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <Styled.Container
      ref={containerRef}
      onClick={_onClick}
      $isClickable
      tabIndex={tabbable ? 0 : undefined}
    >
      <Styled.IconContainer $color={lifeArea.color}>
        <Icon
          icon={lifeArea.iconName ? IconFormIcons[lifeArea.iconName] : Heart}
        />
      </Styled.IconContainer>

      <Styled.Title>{name}</Styled.Title>
    </Styled.Container>
  );
};
